import { Stack, TextField, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { Control, Controller, FieldErrors, FieldValues, UseFormSetValue } from "react-hook-form";
import { Credential } from "../../graphql/types";
import { dummyPassword } from "../../lib/Util";
import { KeyCell } from "./KeyCell";

export const GoogleServiceAccountForm = ({
  control,
  errors,
  mode,
  credential,
  setValue,
  loading,
  editingField,
}: {
  control?: Control;
  errors?: FieldErrors;
  mode?: string;
  credential?: Credential;
  setValue?: UseFormSetValue<FieldValues>;
  loading?: boolean;
  editingField?: string;
}) => {
  const serviceAccountKeyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (serviceAccountKeyRef.current && editingField !== "") {
      serviceAccountKeyRef.current.focus();
    }
  }, [serviceAccountKeyRef, editingField]);

  const validateJson = (value: string) => {
    if (!setValue) return;
    if (mode === "edit" && !value) return;
    if (value) {
      try {
        const json = JSON.parse(value);
        if (json.client_email) {
          setValue("client_email", json.client_email);
        } else {
          setValue("client_email", "");

          return "Please provide a client email attribute in the JSON";
        }
      } catch {
        return "The provided JSON is not valid";
      }
    } else {
      if (mode === "add") {
        setValue("client_email", "");
      }
    }
  };

  const resolveLabel = (value: unknown) => {
    if (!value && credential?.properties_exist?.service_account_key) return dummyPassword;

    return value
      ? undefined
      : ` --- e.g.
                {
                   "type": "service_account",
                  "project_id": "project-name",
                  "private_key_id": "00129023234n23ji4n324349309340",
                  "private_key": "-----BEGIN PRIVATE KEY-----\n...==\n-----END PRIVATE KEY-----\n",
                  "client_email": "clientemail@something.iam.gserviceaccount.com",
                  "client_id": "35987875293582",
                  "auth_uri": "https://accounts.google.com/o/oauth2/auth",
                  "token_uri": "https://oauth2.googleapis.com/token",
                  "auth_provider_x509_cert_url": "https://www.googleapis.com/oauth2/v1/certs",
                  "client_x509_cert_url": "https://www.googleapis.com/robot/v1/metadata/x509/something.iam.gserviceaccount.com",
                  "universe_domain": "googleapis.com"
                }
                `;
  };

  const isRequired = !credential?.properties_exist?.service_account_key;

  return (
    <>
      <Controller
        name="service_account_key"
        control={control}
        rules={{
          required: isRequired,
          validate: validateJson,
        }}
        render={({ field }) => {
          if (mode === "view") {
            return <KeyCell label="Service Account Key (JSON)" value={dummyPassword} />;
          }

          return (
            <Stack direction="column">
              <Typography variant="body.medium.semibold" sx={{ marginBottom: "4px" }}>
                Service Account Key (JSON)
              </Typography>
              <TextField
                {...field}
                label={resolveLabel(field.value)}
                multiline
                rows={10}
                error={errors && Boolean(errors["service_account_key"])}
                margin="normal"
                required={isRequired}
                disabled={(mode === "view" && editingField !== "service_account_key") || loading}
                type="password"
                helperText={errors ? (errors["service_account_key"]?.message as string) : ""}
                data-cy="service_account_key"
                slotProps={{
                  inputLabel: {
                    style: { marginRight: 0.5 },
                    shrink: false,
                  },
                }}
                sx={{ height: "300px" }}
                inputRef={serviceAccountKeyRef}
              />
            </Stack>
          );
        }}
      />
      <Controller
        name="client_email"
        control={control}
        rules={{ required: isRequired }}
        defaultValue={""}
        render={({ field }) => {
          if (mode === "view") {
            return <KeyCell label="Client email" value={field.value} />;
          }

          return (
            <Stack direction="column">
              <Typography variant="body.medium.semibold" sx={{ marginBottom: "4px" }}>
                Client email
              </Typography>
              <Typography
                variant={field.value ? "body.large.semibold" : "body.medium.regular"}
                data-cy="client_email"
                sx={{ color: field.value ? "text50.main" : "text30.main", padding: "12px" }}
              >
                {field.value || "The email will autopopulate here"}
              </Typography>
            </Stack>
          );
        }}
      />
    </>
  );
};
